import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Permission } from '../models/permissions.interface';
import { FeatureFlagName } from './feature-flag/feature-flag.interface';
import { FeatureFlagService } from './feature-flag/feature-flag.service';
import { HotelSettingsService } from './hotel-settings.service';
import { PermissionService } from './permissions/permission.service';
import { PositionService } from './position/position.service';
import { ServiceService } from './service.service';

@Injectable({
  providedIn: 'root',
})
export class AccessService {
  constructor(
    private _permissionService: PermissionService,
    private _serviceService: ServiceService,
    private _hotelSettingsService: HotelSettingsService,
    private _featureFlagService: FeatureFlagService,
    private _positionService: PositionService,
  ) {}

  get canAccessTasks$(): Observable<boolean> {
    return combineLatest([
      this.canAccessGeneralTasks$,
      this.canAccessRoomCleanTasks$,
    ]).pipe(
      map(
        ([canAccessGeneralTasks, canAccessRoomCleanTasks]) =>
          canAccessGeneralTasks || canAccessRoomCleanTasks,
      ),
    );
  }

  get canAccessGeneralTasks$(): Observable<boolean> {
    return combineLatest([
      this._serviceService.hasAtLeastOneHotelWithTasks$,
      this._permissionService.canAccessTasks$,
      this._featureFlagService.isTasksFlagOn$,
    ]).pipe(
      map(
        ([
          hasAtLeastOneHotelWithTasksService,
          hasTasksPermission,
          isFeatureOn,
        ]) => {
          const canAccessGeneralTasks =
            hasAtLeastOneHotelWithTasksService && hasTasksPermission;
          return isFeatureOn && canAccessGeneralTasks;
        },
      ),
    );
  }

  get canAccessRoomCleanTasks$(): Observable<boolean> {
    return combineLatest([
      this._serviceService.hasAtLeastOneHotelWithRealtimeRooms$,
      this._permissionService.canAccessBoardBuilderV2$,
      this._hotelSettingsService.canCreateRoomCleanTasks$,
      this._featureFlagService.isTasksFlagOn$,
    ]).pipe(
      map(
        ([
          hasAtLeastOneHotelWithRealtimeRoomsService,
          hasBoardBuilderV2Permission,
          hasCreateRoomCleanTasksSetting,
          isFeatureOn,
        ]) => {
          const canAccessRoomCleanTasks =
            hasAtLeastOneHotelWithRealtimeRoomsService &&
            hasBoardBuilderV2Permission &&
            hasCreateRoomCleanTasksSetting;
          return isFeatureOn && canAccessRoomCleanTasks;
        },
      ),
    );
  }

  get canAccessHousekeepingTools$(): Observable<boolean> {
    return combineLatest([
      this.canAccessRealtimeRooms$,
      this.canAccessRealtimeRoomsPortfolio$,
    ]).pipe(
      map(([canAccessRealtimeRooms, canAccessRealtimeRoomsPortfolio]) => {
        return canAccessRealtimeRooms || canAccessRealtimeRoomsPortfolio;
      }),
    );
  }

  get canAccessRealtimeRooms$(): Observable<boolean> {
    return combineLatest([
      this._serviceService.hasAtLeastOneHotelWithRealtimeRooms$,
      this._permissionService.canAccessBoardBuilderV2$,
      this._featureFlagService.isRealtimeRoomsFlagOn$,
      this._positionService.hasHousekeepingAttendant$,
    ]).pipe(
      map(
        ([
          hasService,
          hasPermission,
          isFlagOn,
          hasAccessToHousekeepingAttendant,
        ]) => {
          return (
            hasService &&
            hasPermission &&
            isFlagOn &&
            hasAccessToHousekeepingAttendant
          );
        },
      ),
    );
  }

  get canAccessRealtimeRoomsPortfolio$(): Observable<boolean> {
    return combineLatest([
      this._serviceService.hotelsWithRealtimeRoomsService$,
      this._permissionService.canAccessBoardBuilderV2$,
      this._featureFlagService.isRealtimeRoomsPortfolioFlagOn$,
      this._positionService.hasHousekeepingAttendant$,
    ]).pipe(
      map(
        ([
          hotelsWithService,
          hasPermission,
          isFlagOn,
          hasAccessToHousekeepingAttendant,
        ]) => {
          return (
            hotelsWithService.length > 1 &&
            hasPermission &&
            isFlagOn &&
            hasAccessToHousekeepingAttendant
          );
        },
      ),
    );
  }

  get canAccessShiftSwapRequests$(): Observable<boolean> {
    return combineLatest([
      this._featureFlagService.isShiftSwapRequestsFlagOn$,
      this._serviceService.hasAtLeastOneHotelWithCoverageFinderService$,
    ]).pipe(
      map(
        ([
          isShiftSwapRequestsFlagOn,
          hasAtLeastOneHotelWithCoverageFinderService,
        ]) => {
          return (
            isShiftSwapRequestsFlagOn &&
            hasAtLeastOneHotelWithCoverageFinderService
          );
        },
      ),
    );
  }

  get canAccessShiftSwapRequestsDetails$(): Observable<boolean> {
    return combineLatest([
      this.canAccessShiftSwapRequests$,
      this._permissionService.hasPermission$(Permission.CanPublishSchedules),
    ]).pipe(
      map(([canAccessShiftSwapRequests, canPublishSchedules]) => {
        return canAccessShiftSwapRequests && canPublishSchedules;
      }),
    );
  }

  get canAccessOfferOrSwapNotifications$(): Observable<boolean> {
    const isOfferAndSwapNotifications = this._featureFlagService.isFeatureOn(
      FeatureFlagName.offerAndSwapNotifications,
    );
    return isOfferAndSwapNotifications
      ? combineLatest([
          this._hotelSettingsService
            .hasAtLeastOneHotelThatAllowEmployeesToOfferPickUpShifts$,
          this._hotelSettingsService
            .hasAtLeastOneHotelThatAllowEmployeesToSwapShifts$,
        ]).pipe(
          map(
            ([
              hasAtLeastOneHotelThatAllowEmployeesToOfferPickUpShifts,
              hasAtLeastOneHotelThatAllowEmployeesToSwapShifts,
            ]) => {
              return (
                hasAtLeastOneHotelThatAllowEmployeesToOfferPickUpShifts ||
                hasAtLeastOneHotelThatAllowEmployeesToSwapShifts
              );
            },
          ),
        )
      : of(false);
  }

  get canAccessRoomCleanTaskNotes$(): Observable<boolean> {
    return combineLatest([
      this.canAccessRealtimeRooms$,
      this._featureFlagService.isRealtimeRoomsNewTaskStatusesOn$,
    ]).pipe(
      map(
        ([canAccessRealtimeRooms, isRealtimeRoomsNewTaskStatusesOn]) =>
          canAccessRealtimeRooms && isRealtimeRoomsNewTaskStatusesOn,
      ),
    );
  }
}
